// src/utils/agentSession.js
// What the agent website (/#/openhouses) keeps on the agent's own device. Nothing here is a Firebase sign-in:
// outside agents never get one. The session is the server's 30-day token (functions-api stores only its
// hash), kept in localStorage so the agent stays signed in on this phone. Every storage call is wrapped:
// private browsing can refuse or throw, and then the values live in memory for this visit only.
//
//   tes:ohAgentSession  localStorage    { token, expiresAt, email }, dropped once expiresAt has passed
//   tes:ohAgentSkipCrm  localStorage    "1" after Sign Out, so a CRM login in the same browser does not sign
//                                       the agent straight back in; removed when the CRM button is tapped
//   tes:returnTo        sessionStorage  { path: "/openhouses", at }, the CRM login's way back (App.js)
//   tes:hostAgent       localStorage    the retired hosting page's remembered agent, removed on sight
export const SESSION_KEY = "tes:ohAgentSession";
export const SKIP_CRM_KEY = "tes:ohAgentSkipCrm";
export const RETURN_TO_KEY = "tes:returnTo";
export const LEGACY_AGENT_KEY = "tes:hostAgent";
export const RETURN_TO_PATH = "/openhouses";
export const RETURN_TO_MAX_AGE_MS = 30 * 60 * 1000;

const TOKEN_RE = /^[a-f0-9]{64}$/;
// The fallback for storage that refuses: "local:<key>" or "session:<key>" to the string value.
const memory = new Map();

function storeFor(kind) {
  try {
    const s = kind === "session" ? window.sessionStorage : window.localStorage;
    return s || null;
  } catch {
    return null;
  }
}

function getItem(kind, key) {
  const s = storeFor(kind);
  if (s) {
    try {
      const v = s.getItem(key);
      if (v !== null) return v;
    } catch {
      // Refused: the memory copy below is all there is.
    }
  }
  const k = `${kind}:${key}`;
  return memory.has(k) ? memory.get(k) : null;
}

function setItem(kind, key, value) {
  memory.set(`${kind}:${key}`, value);
  const s = storeFor(kind);
  if (!s) return;
  try {
    s.setItem(key, value);
  } catch {
    // Refused (private mode, full): the memory copy carries this visit.
  }
}

function removeItem(kind, key) {
  memory.delete(`${kind}:${key}`);
  const s = storeFor(kind);
  if (!s) return;
  try {
    s.removeItem(key);
  } catch {
    // Nothing to do: it was never readable either.
  }
}

// Tests only: forget the in-memory copies between tests.
export function clearMemoryCopies() {
  memory.clear();
}

// The stored session, or null when there is none, it is malformed, or it has run out (then it is removed).
export function readSession(now = Date.now()) {
  const raw = getItem("local", SESSION_KEY);
  if (!raw) return null;
  let s = null;
  try {
    s = JSON.parse(raw);
  } catch {
    s = null;
  }
  const ok =
    !!s &&
    typeof s.token === "string" &&
    TOKEN_RE.test(s.token) &&
    typeof s.expiresAt === "number" &&
    s.expiresAt > now;
  if (!ok) {
    removeItem("local", SESSION_KEY);
    return null;
  }
  return { token: s.token, expiresAt: s.expiresAt, email: typeof s.email === "string" ? s.email : "" };
}

export function saveSession({ token, expiresAt, email }) {
  setItem(
    "local",
    SESSION_KEY,
    JSON.stringify({ token: String(token || ""), expiresAt: Number(expiresAt) || 0, email: String(email || "") }),
  );
}

export function clearSession() {
  removeItem("local", SESSION_KEY);
}

export function readSkipCrm() {
  return getItem("local", SKIP_CRM_KEY) === "1";
}

export function setSkipCrm() {
  setItem("local", SKIP_CRM_KEY, "1");
}

export function clearSkipCrm() {
  removeItem("local", SKIP_CRM_KEY);
}

// The retired hosting page remembered the agent's name, email and phone here; the agent website never reads
// it, so it is removed the first time the new page opens.
export function forgetLegacyAgent() {
  removeItem("local", LEGACY_AGENT_KEY);
}

// "Use Your CRM Login" with nobody signed in to the CRM: remember to come back here after the login page.
export function writeReturnTo(now = Date.now()) {
  setItem("session", RETURN_TO_KEY, JSON.stringify({ path: RETURN_TO_PATH, at: now }));
}

// App.js, the moment a CRM sign-in lands: true exactly when a fresh (under 30 minutes) return to the agent
// website is waiting. The key is removed either way, so it is honoured once, and only that one path is ever
// honoured.
export function takeReturnTo(now = Date.now()) {
  const raw = getItem("session", RETURN_TO_KEY);
  if (!raw) return false;
  removeItem("session", RETURN_TO_KEY);
  try {
    const r = JSON.parse(raw);
    return (
      !!r &&
      r.path === RETURN_TO_PATH &&
      typeof r.at === "number" &&
      r.at <= now &&
      now - r.at < RETURN_TO_MAX_AGE_MS
    );
  } catch {
    return false;
  }
}
